import { ApiClient } from '@/shared/api/client';

export interface GrafanaDashboard {
  /** 화면에 보이는 이름. 서버 설정이 정본이라 화면이 목록을 코드로 들지 않는다. */
  name: string;
  /** Grafana 대시보드 uid — 매니페스트(ConfigMap)가 정본이고 여기선 계약면으로만 쓴다. */
  uid: string;
}

export interface GrafanaPanel {
  id: number;
  title: string;
  type: string;
  /** Grafana 그리드 폭(24 기준). 콘솔이 열 배치를 정할 때 참고만 한다. */
  width: number;
}

export interface PanelSection {
  /** 대시보드의 row 제목. row 앞에 놓인 패널들은 빈 제목 섹션에 들어간다. */
  title: string;
  panels: GrafanaPanel[];
}

interface RawPanel {
  id?: number;
  title?: string;
  type?: string;
  gridPos?: { w?: number };
}

export class GrafanaApi {
  static async getDashboards(): Promise<GrafanaDashboard[]> {
    const { dashboards } = await ApiClient.getJson<{ dashboards: GrafanaDashboard[] }>('/grafana/dashboards');
    return dashboards;
  }

  /**
   * 패널 목록은 **그라파나에서 읽는다** — 매니페스트가 정본이므로 콘솔이 패널을 코드로 들면
   * 대시보드가 바뀔 때 조용히 어긋난다. row는 섹션 경계로만 쓴다.
   */
  static async getPanelSections(uid: string): Promise<PanelSection[]> {
    const res = await fetch(`/api/dashboards/uid/${encodeURIComponent(uid)}`, {
      headers: { accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`대시보드 정의를 읽지 못했다 (HTTP ${res.status})`);
    const body = (await res.json()) as { dashboard?: { panels?: RawPanel[] } };
    const sections: PanelSection[] = [{ title: '', panels: [] }];
    for (const panel of body.dashboard?.panels ?? []) {
      if (panel.type === 'row') {
        sections.push({ title: panel.title ?? '', panels: [] });
        continue;
      }
      // id 없는 패널은 d-solo로 지목할 수 없다 — 대시보드 JSON에 id가 있어야 한다.
      if (typeof panel.id !== 'number') continue;
      sections[sections.length - 1].panels.push({
        id: panel.id,
        title: panel.title ?? '',
        type: panel.type ?? '',
        width: panel.gridPos?.w ?? 12,
      });
    }
    return sections.filter((section) => section.panels.length > 0);
  }

  /**
   * 프록시 도달 확인. iframe은 HTTP 오류로 `onError`를 내지 않아 실패가 빈 화면으로 보이므로,
   * 가벼운 경로를 먼저 찔러 도달 여부를 가른다.
   */
  static async probe(): Promise<boolean> {
    try {
      const res = await fetch('/api/health', { headers: { accept: 'application/json' } });
      return res.ok;
    } catch {
      return false;
    }
  }
}

export class GrafanaUrl {
  static readonly SEARCH_ES_UID = 'search-es';

  /** Search ES dashboard의 변수·timezone·refresh 기본값. `$__all`은 Grafana URL 인자로 인코딩한다. */
  static searchEsContext(uid: string): string {
    if (uid !== GrafanaUrl.SEARCH_ES_UID) return '';
    const params = new URLSearchParams({
      'var-cluster': 'search-es',
      'var-name': '$__all',
      timezone: 'browser',
      refresh: '30s',
    });
    return params.toString();
  }

  /**
   * iframe 주소는 항상 same-origin 상대 경로다 — 프런트는 그라파나 호스트를 모른다.
   * 경로는 그라파나가 정한 그대로다(콘솔이 서브경로를 씌우지 않는다 — 운영 그라파나 무변경).
   */
  static dashboardSrc(uid: string): string {
    const context = GrafanaUrl.searchEsContext(uid);
    const defaults = context ? `from=now-6h&to=now&${context}&` : '';
    return `/d/${encodeURIComponent(uid)}?${defaults}kiosk&theme=light`;
  }

  /**
   * "새 탭" 링크 전용 — 콘솔 셸 밖(풀페이지)이라 라이트 테마를 강제하지 않는다.
   * `theme=light`는 콘솔 카드 안에 임베드될 때만 의미가 있다(GrafanaUrl.dashboardSrc).
   */
  static dashboardHref(uid: string): string {
    const context = GrafanaUrl.searchEsContext(uid);
    const defaults = context ? `from=now-6h&to=now&${context}&` : '';
    return `/d/${encodeURIComponent(uid)}?${defaults}kiosk`;
  }

  /**
   * 패널 카드의 "크게 보기" 링크 — 그라파나에서 그 패널만 펼친 화면으로 연다.
   * `kiosk`를 씌우지 않는다: 이 버튼의 목적이 임베드에서 못 쓰는 그라파나 기능(패널 메뉴·Inspect·
   * 시간 범위 조작)을 쓰는 것이라, 크롬을 감추면 버튼을 만든 이유가 사라진다.
   */
  static panelHref(uid: string, panelId: number, range: string): string {
    const context = GrafanaUrl.searchEsContext(uid);
    return `/d/${encodeURIComponent(uid)}?viewPanel=${panelId}&from=now-${range}&to=now${context ? `&${context}` : ''}`;
  }

  static panelSrc(uid: string, panelId: number, range: string): string {
    const context = GrafanaUrl.searchEsContext(uid);
    return `/d-solo/${encodeURIComponent(uid)}?panelId=${panelId}&from=now-${range}&to=now${context ? `&${context}` : ''}&theme=light`;
  }
}
